import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useTailwindBreakpoint, useToasts } from '@headless-workspace/core-ui';
import { GlowClickable, GlowSkeleton, GlowTextBody, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { LanguageHelpers, StringHelpers } from '@headless-workspace/utils';
import { ProductReview, ReviewTranslationPayload } from '../../../../../server';
import { useTranslateReviewMutation } from '../useTranslateReviewMutation';

const MAX_CONTENT_LENGTH_MOBILE = 104;
const MAX_CONTENT_LENGTH = 124;
const REVIEW_TITLE_TRANSLATION_KEY = 'reviewTitle';
const REVIEW_CONTENT_TRANSLATION_KEY = 'reviewContent';

type ReviewTitleAndContentProps = PropsWithLocale & {
    title: ProductReview['title'];
    content: ProductReview['content'];
    translatable: ProductReview['translatable'];
    truncateContent?: boolean;
};

export const ReviewTitleAndContent: Component<ReviewTitleAndContentProps> = ({
    title,
    content,
    translatable,
    truncateContent = true,
    locale,
}) => {
    const t = useTranslations('Discover.Pdp.ratingAndReviews');
    const isMobile = useTailwindBreakpoint('tablet', true, true);
    const { notifyError } = useToasts();

    const { translate, isTranslating } = useTranslateReviewMutation();

    const [displayedContent, setDisplayedContent] = useState({
        isTranslated: false,
        title,
        content,
        isClamped: truncateContent,
    });

    const maxLength = isMobile ? MAX_CONTENT_LENGTH_MOBILE : MAX_CONTENT_LENGTH;
    const shouldClamp = content ? content.length > maxLength : false;

    const toggleReviewTranslation = useCallback(async () => {
        if (displayedContent.isTranslated) {
            setDisplayedContent((prevState) => ({
                ...prevState,
                isTranslated: false,
                title,
                content,
            }));
            return;
        }

        const reviewTranslationPayload: ReviewTranslationPayload = {
            data: [
                {
                    label: REVIEW_TITLE_TRANSLATION_KEY,
                    text: title,
                },
            ],
            target: LanguageHelpers.parseLanguage(locale),
        };

        if (content) {
            reviewTranslationPayload.data.push({
                label: REVIEW_CONTENT_TRANSLATION_KEY,
                text: content,
            });
        }

        const translatedResult = await translate(reviewTranslationPayload);

        if (translatedResult.type === 'failure') {
            notifyError(t('error.translation'));
            return;
        }

        if (translatedResult.type === 'success') {
            const translatedTitle = translatedResult.data[REVIEW_TITLE_TRANSLATION_KEY];
            const translatedContent = translatedResult.data[REVIEW_CONTENT_TRANSLATION_KEY];

            setDisplayedContent((prevState) => {
                return {
                    ...prevState,
                    isTranslated: true,
                    title: translatedTitle,
                    content: translatedContent,
                };
            });
        }
    }, [displayedContent.isTranslated, title, locale, content, translate, notifyError, t]);

    const seeMoreOnClick = () => {
        if (!truncateContent) {
            return;
        }

        setDisplayedContent((prevState) => ({ ...prevState, isClamped: !prevState.isClamped }));
    };

    return (
        <div className={'flex flex-col gap-0.5 px-1'}>
            {isTranslating ? (
                <GlowSkeleton height={'1.5rem'} containerClassName={'w-full'} />
            ) : (
                <GlowTextBody text={displayedContent.title} fontWeight={'bold'} />
            )}
            {displayedContent.content && (
                <div className={'flex flex-row'}>
                    {isTranslating ? (
                        <GlowSkeleton height={'1.5rem'} containerClassName={'w-full'} line={2} />
                    ) : (
                        <GlowTextBody
                            className={'break-all'}
                            innerHTML={
                                <>
                                    {StringHelpers.getClampedContent(
                                        displayedContent.content,
                                        maxLength,
                                        displayedContent.isClamped,
                                    )}
                                    {shouldClamp && (
                                        <GlowClickable
                                            content={{
                                                labelElement: (
                                                    <GlowTextBody
                                                        text={
                                                            displayedContent.isClamped
                                                                ? t('action.seeMore')
                                                                : t('action.seeLess')
                                                        }
                                                        textDecoration={'underline'}
                                                        TagName={'span'}
                                                    />
                                                ),
                                            }}
                                            onClick={seeMoreOnClick}
                                            className={'pl-0.25 !inline w-fit'}
                                        />
                                    )}
                                </>
                            }
                        />
                    )}
                </div>
            )}
            {translatable && (
                <GlowClickable
                    content={{
                        labelElement: (
                            <GlowTextBody
                                text={
                                    displayedContent.isTranslated
                                        ? t('action.showOriginal')
                                        : t('action.translateWithGoogle')
                                }
                                textDecoration={'underline'}
                                TagName={'span'}
                            />
                        ),
                    }}
                    onClick={toggleReviewTranslation}
                />
            )}
        </div>
    );
};
